<template>
    <dialog-modal v-if="shouldRender" :show="show">
        <template #title>
            {{ trans('qr_code_scanner.modal.header') }}
        </template>
        <template #subtitle>
            {{ trans('qr_code_scanner.modal.description') }}
        </template>
        <qr-code-scanner
            v-if="show"
            :key="componentKey"
            ref="scanner"
            :valid="isValidQrCode"
            @failure="onError"
            @reset="isValidQrCode = undefined"
            @success="onScanSuccess"
        />
        <form
            v-if="scannerType !== scanIdType"
            @submit.prevent="onSuccess(form.curieId)"
        >
            <row class="mt-3">
                <column>
                    <jet-input
                        v-model="form.curieId"
                        :horizontal="false"
                        :invalidFeedback="form.errors.curieId"
                        :label="
                            trans('assets.details.curie_id.label')
                        "
                        :placeholder="
                            trans(
                                'assets.details.curie_id.placeholder',
                            )
                        "
                        :required="true"
                        append-classes="bg-primary border-primary"
                        class="bg-append-primary"
                    >
                        <template #append>
                            <div class="text-white d-flex align-items-center">
                                <LogoCurieCC :height="16" :width="16" />
                            </div>
                        </template>
                    </jet-input>
                </column>
            </row>
        </form>
        <template #footer>
            <jet-button variant="tertiary" @click="close"
                >{{ trans('general.action.cancel') }}
            </jet-button>
            <template v-if="scannerType !== scanIdType">
                <jet-button
                    v-if="form.errors.curieId"
                    :is-processing="form.processing"
                    @click.stop="reset"
                >
                    {{ trans('general.action.retry') }}
                </jet-button>
                <jet-button
                    v-else
                    :disabled="!isFormSubmittable"
                    :is-processing="form.processing"
                    @click.stop="onSuccess(form.curieId)"
                >
                    {{ primaryButtonText }}
                </jet-button>
            </template>
        </template>
    </dialog-modal>
</template>
<script>
import QrCodeScanner from '@/Components/QrCodeScanner.vue';
import JetButton from '@/Jetstream/Button.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import JetInput from '@/Jetstream/Input.vue';
import {$http} from '@/bootstrap.js';
import {eventBus, events} from '@/eventBus.js';
import {CInputGroupText} from '@coreui/vue-pro/dist/esm/components/form/index.js';
import LogoCurieCC from '../../icons/LogoCurieCC.vue';

export const CurieIdScannerType = {
    attachCurieId: 'attachCurieId',
    searchAsset: 'searchAsset',
    scanId: 'scanId',
};

export default {
    components: {
        LogoCurieCC,
        CInputGroupText,
        QrCodeScanner,
        DialogModal,
        JetInput,
        JetButton,
    },

    data() {
        return {
            componentKey: 0,
            show: false,
            shouldRender: false,
            assetId: null,
            isValidQrCode: undefined,
            scannerType: CurieIdScannerType.searchAsset,
            form: this.$inertia.form({
                curieId: null,
                scannedCode: null,
            }),
            onSuccessfulScan: null,
        };
    },
    computed: {
        primaryButtonText() {
            switch (this.scannerType) {
                case CurieIdScannerType.attachCurieId:
                    return this.trans('general.action.save');
                case CurieIdScannerType.searchAsset:
                    return this.trans('general.action.go');
            }
        },
        scanIdType() {
            return CurieIdScannerType.scanId;
        },
    },
    mounted() {
        eventBus.$on(events.openQrCodeScanner, (config) => {
            this.resetForm();
            this.assetId = config?.assetId;

            if (
                config?.type &&
                !Object.values(CurieIdScannerType).includes(config.type)
            ) {
                throw new Error(`Invalid scanner type ${config.type}`);
            }

            this.scannerType = config?.type || CurieIdScannerType.searchAsset;
            this.onSuccessfulScan = config?.onSuccessfulScan || null;

            this.shouldRender = true;
            this.$nextTick(() => {
                this.show = true;
            });
        });
    },

    methods: {
        onScanSuccess(code) {
            this.form.scannedCode = code;
            this.onSuccess(code);
        },
        onSuccess(code) {
            this.form.curieId = code;

            switch (this.scannerType) {
                case CurieIdScannerType.attachCurieId:
                    this.attachCurieId();
                    break;
                case CurieIdScannerType.searchAsset:
                    this.searchAsset();
                    break;
                case CurieIdScannerType.scanId:
                    this.onSuccessfulScan(code, this);
                    break;
            }
        },
        onError() {
            this.$emit('error');
        },
        reset() {
            this.resetForm();
            this.componentKey += 1;
        },
        resetForm() {
            this.form = this.$inertia.form({
                curieId: null,
                scannedCode: null,
            })
            this.isValidQrCode = undefined;
        },
        searchAsset() {
            this.form.processing = true;

            $http
                .get(
                    route('assets.search', {
                        searchTerm: this.form.curieId,
                        qr:
                            this.form.scannedCode ===
                            this.form.curieId ? 1 : undefined,
                    }),
                )
                .then((resp) => {
                    const asset = resp.data;
                    this.isValidQrCode = true;
                    this.close();
                    this.$nextTick(() => {
                        this.$inertia.visit(route('assets.show', asset.id));
                    });
                })
                .catch((err) => {
                    this.isValidQrCode = false;
                    this.form.errors.curieId = this.trans(
                        'assets.overview.scanner.error.asset_not_found',
                    );
                })
                .finally(() => {
                    this.form.processing = false;
                });
        },
        attachCurieId() {
            this.form.put(this.route('assets.assign_pid', this.assetId), {
                onSuccess: () => {
                    this.isValidQrCode = true;

                    setTimeout(() => {
                        this.close();
                    }, 300);
                },
                onError: () => {
                    this.isValidQrCode = false;
                },
            });
        },
        close: function () {
            this.show = false;

            setTimeout(() => {
                this.shouldRender = false;
                this.scannerType = null;
                this.onSuccessfulScan = null;
            }, 300);
        },
    },
};
</script>
